/**
 * XR Service for managing WebXR sessions
 */

import { EventEmitter } from '@/components/SplatViewer/event-emitter'
import { XRWorkerManager } from './xr-worker'
import { createProgram, getShaderUniforms, getShaderAttributes, xrVertexShaderSource, xrFragmentShaderSource } from './xr-shaders'
import { multiply4, getFocalFromProjection, getDefaultWorldTransform, parseWorldTransformFromHash, serializeWorldTransformToHash } from './xr-utils'
import type { XRSessionState, XROptions, XRWorkerMessage } from '@/types/xr'

export class XRService extends EventEmitter {
  private session: XRSession | null = null
  private referenceSpace: XRReferenceSpace | null = null
  private gl: WebGL2RenderingContext | null = null
  private program: WebGLProgram | null = null
  private uniforms: ReturnType<typeof getShaderUniforms> | null = null
  private attributes: ReturnType<typeof getShaderAttributes> | null = null
  private workerManager: XRWorkerManager | null = null
  private worldTransform: Float32Array
  private vertexCount = 0
  private texture: WebGLTexture | null = null
  private indexBuffer: WebGLBuffer | null = null
  private vertexBuffer: WebGLBuffer | null = null
  private canvas: HTMLCanvasElement | null = null
  private modelUrl: string | null = null
  private options: XROptions
  
  // State
  private state: XRSessionState = {
    isSupported: false,
    isActive: false,
    sessionType: null,
    error: null
  }

  constructor(options: XROptions = {}) {
    super()
    this.options = options
    this.worldTransform = parseWorldTransformFromHash() || getDefaultWorldTransform()
  }

  /**
   * Check if XR is supported
   */
  async checkSupport(): Promise<XRSessionState> {
    if (!navigator.xr) {
      this.state.error = 'WebXR not supported'
      return this.state
    }

    try {
      const vrSupported = await navigator.xr.isSessionSupported('immersive-vr')
      const arSupported = await navigator.xr.isSessionSupported('immersive-ar')
      
      this.state.isSupported = vrSupported || arSupported
      
      if (!this.state.isSupported) {
        this.state.error = 'No XR devices detected'
      }
    } catch (error) {
      this.state.error = 'Failed to check XR support'
      console.error('XR support check failed:', error)
    }

    return this.state
  }

  /**
   * Start XR session
   */
  async startSession(canvas: HTMLCanvasElement, modelUrl: string, sessionType?: 'vr' | 'ar'): Promise<void> {
    if (this.state.isActive) {
      throw new Error('XR session already active')
    }

    this.canvas = canvas
    this.modelUrl = modelUrl

    // Determine session type
    let xrSessionType: XRSessionMode = 'immersive-vr'
    if (sessionType === 'ar' || (this.options.sessionType === 'ar')) {
      xrSessionType = 'immersive-ar'
    } else if (this.options.sessionType === 'auto') {
      const arSupported = await navigator.xr!.isSessionSupported('immersive-ar')
      xrSessionType = arSupported ? 'immersive-ar' : 'immersive-vr'
    }

    try {
      // Request XR session
      this.session = await navigator.xr!.requestSession(xrSessionType)
      this.state.sessionType = xrSessionType
      
      // Try to get existing context first
      let gl = canvas.getContext('webgl2') as WebGL2RenderingContext | null
      
      // Check if existing context is XR compatible
      if (gl && !(gl as any).xrCompatible) {
        console.warn('Existing WebGL context is not XR compatible. This may cause issues.')
        // Try to use it anyway - the context in splatv-module.js should now be XR compatible
      }
      
      // If no context exists, create a new one
      if (!gl) {
        gl = canvas.getContext('webgl2', {
          xrCompatible: true,
          antialias: true,
          alpha: true,
          premultipliedAlpha: false
        })
      }
      
      this.gl = gl
      
      if (!this.gl) {
        throw new Error('Failed to get or create WebGL2 context')
      }

      // Initialize XR rendering
      await this.initializeXRRendering()
      
      // Setup session event handlers
      this.session.addEventListener('end', () => this.handleSessionEnd())
      
      // Update render state
      this.session.updateRenderState({
        baseLayer: new XRWebGLLayer(this.session, this.gl, {
          alpha: true,
          antialias: true
        })
      })
      
      // Get reference space
      this.referenceSpace = await this.session.requestReferenceSpace('local')
      
      // Start loading model
      await this.loadModel()
      
      // Update state
      this.state.isActive = true
      this.state.error = null
      
      // Emit session start event
      this.emit('sessionStart')
      if (this.options.onSessionStart) {
        this.options.onSessionStart()
      }
      
      // Start render loop
      this.session.requestAnimationFrame(this.onXRFrame.bind(this))
      
    } catch (error) {
      this.state.error = error instanceof Error ? error.message : 'Failed to start XR session'
      console.error('XR session start failed:', error)
      throw error
    }
  }

  /**
   * Initialize XR rendering pipeline
   */
  private async initializeXRRendering() {
    if (!this.gl) return

    // Create shader program
    this.program = createProgram(this.gl, xrVertexShaderSource, xrFragmentShaderSource)
    if (!this.program) {
      throw new Error('Failed to create XR shader program')
    }

    this.gl.useProgram(this.program)

    // Get uniform and attribute locations
    this.uniforms = getShaderUniforms(this.gl, this.program)
    this.attributes = getShaderAttributes(this.gl, this.program)

    // Setup GL state
    this.gl.disable(this.gl.DEPTH_TEST)
    this.gl.enable(this.gl.BLEND)
    this.gl.blendFuncSeparate(
      this.gl.ONE_MINUS_DST_ALPHA,
      this.gl.ONE,
      this.gl.ONE_MINUS_DST_ALPHA,
      this.gl.ONE
    )
    this.gl.blendEquationSeparate(this.gl.FUNC_ADD, this.gl.FUNC_ADD)

    // Create vertex buffer for quad
    const triangleVertices = new Float32Array([-2, -2, 2, -2, 2, 2, -2, 2])
    this.vertexBuffer = this.gl.createBuffer()
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vertexBuffer)
    this.gl.bufferData(this.gl.ARRAY_BUFFER, triangleVertices, this.gl.STATIC_DRAW)
    this.gl.enableVertexAttribArray(this.attributes.position)
    this.gl.vertexAttribPointer(this.attributes.position, 2, this.gl.FLOAT, false, 0, 0)

    // Create texture
    this.texture = this.gl.createTexture()
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
    if (this.uniforms.u_texture) {
      this.gl.uniform1i(this.uniforms.u_texture, 0)
    }

    // Create index buffer
    this.indexBuffer = this.gl.createBuffer()
    this.gl.enableVertexAttribArray(this.attributes.index)
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.indexBuffer)
    this.gl.vertexAttribIPointer(this.attributes.index, 1, this.gl.INT, 0, 0)
    this.gl.vertexAttribDivisor(this.attributes.index, 1)

    // Create worker for depth sorting
    this.workerManager = new XRWorkerManager()
    this.workerManager.setMessageHandler(this.handleWorkerMessage.bind(this))
  }

  /**
   * Load model data
   */
  private async loadModel() {
    if (!this.modelUrl) return

    try {
      const response = await fetch(this.modelUrl, { mode: 'cors', credentials: 'omit' })
      if (!response.ok) {
        throw new Error(`Failed to load model: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('Failed to get response reader')
      }

      // Read chunks and process
      await this.readChunks(reader, [{ size: 8, type: 'magic' }])
      
    } catch (error) {
      console.error('Model loading failed:', error)
      this.state.error = 'Failed to load model'
      throw error
    }
  }

  /**
   * Read chunks from stream
   */
  private async readChunks(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    chunks: Array<{ size: number; type: string }>
  ) {
    let chunk = chunks.shift()
    if (!chunk) return
    
    let buffer = new Uint8Array(chunk.size)
    let offset = 0
    
    while (chunk) {
      const { done, value } = await reader.read()
      if (done) break
      
      let data = value
      while (data.length + offset >= chunk.size) {
        buffer.set(data.subarray(0, chunk.size - offset), offset)
        data = data.subarray(chunk.size - offset)
        this.handleChunk(chunk, buffer.buffer, 0, chunks)
        chunk = chunks.shift()
        if (!chunk) break
        buffer = new Uint8Array(chunk.size)
        offset = 0
      }
      
      if (!chunk) break
      buffer.set(data, offset)
      offset += data.length
      this.handleChunk(chunk, buffer.buffer, buffer.byteLength - offset, chunks)
    }
    
    if (chunk) this.handleChunk(chunk, buffer.buffer, 0, chunks)
  }

  /**
   * Handle chunk data
   */
  private handleChunk(chunk: any, buffer: ArrayBuffer, remaining: number, chunks: any[]) {
    if (!remaining && chunk.type === 'magic') {
      const intView = new Uint32Array(buffer)
      if (intView[0] !== 0x674b) {
        throw new Error('Invalid splatv file format')
      }
      chunks.push({ size: intView[1], type: 'chunks' })
    } else if (!remaining && chunk.type === 'chunks') {
      for (const chunkInfo of JSON.parse(new TextDecoder('utf-8').decode(buffer))) {
        chunks.push(chunkInfo)
        if (chunkInfo.type === 'splat') {
          // Start rendering when splat data is detected
          this.emit('modelLoaded')
        }
      }
    } else if (chunk.type === 'splat') {
      if (this.vertexCount === 0 || remaining === 0) {
        this.vertexCount = Math.floor((buffer.byteLength - remaining) / 4 / 16)
        
        // Send to worker for sorting
        if (this.workerManager) {
          this.workerManager.updateTexture(new Float32Array(buffer), remaining)
        }
        
        // Update texture
        if (this.gl && this.texture) {
          const texdata = new Uint32Array(buffer)
          this.gl.activeTexture(this.gl.TEXTURE0)
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
          this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_S, this.gl.CLAMP_TO_EDGE)
          this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_WRAP_T, this.gl.CLAMP_TO_EDGE)
          this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST)
          this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST)
          this.gl.texImage2D(
            this.gl.TEXTURE_2D,
            0,
            this.gl.RGBA32UI,
            chunk.texwidth,
            chunk.texheight,
            0,
            this.gl.RGBA_INTEGER,
            this.gl.UNSIGNED_INT,
            texdata
          )
        }
      }
    }
  }

  /**
   * Handle worker message
   */
  private handleWorkerMessage(data: XRWorkerMessage) {
    if (data.depthIndex && this.gl && this.indexBuffer) {
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.indexBuffer)
      this.gl.bufferData(this.gl.ARRAY_BUFFER, data.depthIndex, this.gl.DYNAMIC_DRAW)
      this.vertexCount = data.vertexCount || 0
    }
  }

  /**
   * XR frame callback
   */
  private onXRFrame(_time: number, frame: XRFrame) {
    if (!this.session || !this.gl || !this.program || !this.uniforms) return

    const session = frame.session
    const pose = frame.getViewerPose(this.referenceSpace!)
    if (!pose) {
      session.requestAnimationFrame(this.onXRFrame.bind(this))
      return
    }

    const glLayer = session.renderState.baseLayer!
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, glLayer.framebuffer)
    
    // Only clear in VR mode, not AR (AR needs camera feed)
    if (this.state.sessionType === 'immersive-vr') {
      this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT)
    }

    // Set time uniform
    if (this.uniforms.time) {
      this.gl.uniform1f(this.uniforms.time, Math.sin(Date.now() / 1000) / 2 + 1 / 2)
    }

    // Render for each eye
    for (const view of pose.views) {
      const viewport = glLayer.getViewport(view)
      if (!viewport) continue
      
      this.gl.viewport(viewport.x, viewport.y, viewport.width, viewport.height)
      
      const projectionMatrix = view.projectionMatrix
      if (this.uniforms.projection) {
        this.gl.uniformMatrix4fv(this.uniforms.projection, false, projectionMatrix)
      }
      
      if (this.uniforms.viewport) {
        this.gl.uniform2fv(this.uniforms.viewport, new Float32Array([viewport.width, viewport.height]))
      }
      
      if (this.uniforms.focal) {
        const focal = getFocalFromProjection(projectionMatrix, viewport)
        this.gl.uniform2fv(this.uniforms.focal, new Float32Array(focal))
      }
      
      // Apply world transform
      const transformedView = multiply4(view.transform.inverse.matrix, this.worldTransform)
      if (this.uniforms.view) {
        this.gl.uniformMatrix4fv(this.uniforms.view, false, transformedView)
      }
      
      // Draw
      if (this.vertexCount > 0) {
        this.gl.drawArraysInstanced(this.gl.TRIANGLE_FAN, 0, 4, this.vertexCount)
      }
      
      // Update worker with view projection
      if (this.workerManager) {
        const viewProj = multiply4(projectionMatrix, transformedView)
        this.workerManager.updateView(viewProj)
      }
    }

    // Save world transform on click
    if (this.canvas) {
      this.canvas.onclick = () => {
        serializeWorldTransformToHash(this.worldTransform)
      }
    }

    session.requestAnimationFrame(this.onXRFrame.bind(this))
  }

  /**
   * End XR session
   */
  async endSession(): Promise<void> {
    if (this.session) {
      await this.session.end()
    }
  }

  /**
   * Handle session end
   */
  private handleSessionEnd() {
    // Clean up resources
    if (this.workerManager) {
      this.workerManager.terminate()
      this.workerManager = null
    }

    if (this.gl) {
      if (this.texture) this.gl.deleteTexture(this.texture)
      if (this.indexBuffer) this.gl.deleteBuffer(this.indexBuffer)
      if (this.vertexBuffer) this.gl.deleteBuffer(this.vertexBuffer)
      if (this.program) this.gl.deleteProgram(this.program)
    }

    this.session = null
    this.referenceSpace = null
    this.gl = null
    this.program = null
    this.texture = null
    this.indexBuffer = null
    this.vertexBuffer = null
    this.vertexCount = 0

    // Update state
    this.state.isActive = false
    this.state.sessionType = null

    // Emit session end event
    this.emit('sessionEnd')
    if (this.options.onSessionEnd) {
      this.options.onSessionEnd()
    }
  }

  /**
   * Get current state
   */
  getState(): XRSessionState {
    return { ...this.state }
  }

  /**
   * Set world transform
   */
  setWorldTransform(transform: Float32Array) {
    this.worldTransform = transform
  }
}